import { Group } from "@mantine/core";
import {
  IconInfoCircle,
  type IconProps,
  type TablerIcon
} from "@tabler/icons-react";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { THEME_VARS } from "../../utils/constants";
import "./IconHelpText.scss";
import { Popover, type PopoverPosition } from "./Popover/Popover";

interface IconHelpTextProps extends Pick<IconProps, "aria-label"> {
  children?: React.ReactNode;
  className?: string;
  Icon: TablerIcon;
  iconColor?: string;
  helpText: string;
  popoverPosition: PopoverPosition;
  size?: IconProps["size"];
}

function IconHelpTextPopover({
  "aria-label": ariaLabel,
  children,
  className,
  helpText,
  Icon,
  iconColor,
  popoverPosition = "left",
  size
}: IconHelpTextProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function togglePopover(e?: React.MouseEvent) {
    setPopoverIsOpen(!popoverIsOpen);
    e?.stopPropagation();
  }

  function handleClickOutside(event: MouseEvent) {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as HTMLDivElement)
    ) {
      setPopoverIsOpen(false);
    }
  }

  const isInfo = Icon === IconInfoCircle;
  const icon = (
    <Icon
      aria-label={ariaLabel}
      className={classnames(className, "help-icon-clickable")}
      color={
        iconColor ?? (isInfo ? THEME_VARS.primary[6] : THEME_VARS.neutral[8])
      }
      role="img"
      size={size}
      onClick={children ? undefined : togglePopover}
    />
  );

  return (
    <Popover
      opened={popoverIsOpen}
      position={popoverPosition}
      ref={popoverRef}
      text={helpText}
      onOpenModal={togglePopover}
    >
      {children ? (
        <Group align="center" gap="xs" wrap="nowrap" onClick={togglePopover}>
          {icon}
          {children}
        </Group>
      ) : (
        icon
      )}
    </Popover>
  );
}

export { IconHelpTextPopover };
